import React from 'react';
import NProgress from 'nprogress';
import { useRouter } from 'next/router';
import { useMutation } from '@apollo/client';
import { useNotify } from '../../../hooks';
import { Currency } from '../../../models';
import { FormButtons } from '../../shared/Forms';
import { CREATE_CURRENCY, UPDATE_CURRENCY } from '../../../graphql/mutation';
import CurrencyFormFields from './CurrencyFormFields';
import { validateCurrencyForm } from '../../../lib/formValidations/validateCurrencyForm';

interface CurrencyFormProps {
  currency?: Currency;
}

function CurrencyForm({ currency }: CurrencyFormProps) {
  const notify = useNotify();
  const router = useRouter();
  const [disabled, setDisabled] = React.useState(false);
  const [formState, setFormState] = React.useState<Partial<Currency>>(
    currency ?? {}
  );

  const [createCurrencyMutation] = useMutation<{
    createCurrency: {
      record: Currency;
    };
  }>(CREATE_CURRENCY);

  const [updateCurrencyMutation] = useMutation<{
    updateCurrency: {
      record: Currency;
    };
  }>(UPDATE_CURRENCY);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (disabled) return;
    try {
      NProgress.start();
      setDisabled(true);

      const error = validateCurrencyForm(formState);
      if (error) {
        return notify(error, 'error');
      }

      const currencyData = {
        ...formState,
        rate: Number(formState.rate),
      };

      if (!currency?._id) {
        createCurrency(currencyData);
      } else {
        updateCurrency(currencyData);
      }
    } catch (error) {
      notify(error, 'error');
    } finally {
      NProgress.done();
      setDisabled(false);
    }
  };

  const createCurrency = async (currencyData: Partial<Currency>) => {
    try {
      const { data } = await createCurrencyMutation({
        variables: {
          record: {
            ...currencyData,
          },
        },
      });
      if (data?.createCurrency?.record?._id) {
        notify('La moneda se ha creado con éxito', 'success');
        router.push('/app/settings/currencies');
      } else {
        notify('Ha ocurrido un error al crear la categoría', 'error');
      }
    } catch (error) {
      return notify(error.message, 'error');
    }
  };

  const updateCurrency = async (currencyData: Partial<Currency>) => {
    delete currencyData._id;
    try {
      const { data } = await updateCurrencyMutation({
        variables: {
          record: {
            ...currencyData,
            __typename: undefined,
          },
          filter: {
            _id: currency._id,
          },
        },
      });
      if (data?.updateCurrency?.record?._id) {
        notify('Moneda actualizada con éxito', 'success');
        router.push('/app/settings/currencies');
      } else {
        notify('Ha ocurrido un error al actualizar la categoría', 'error');
      }
    } catch (error) {
      return notify(error.message, 'error');
    }
  };

  const toggleActive = () => updateCurrency({ active: !currency.active });

  return (
    <form
      method="POST"
      onSubmit={onSubmit}
      className="flex flex-col w-full space-y-10"
    >
      <CurrencyFormFields formState={formState} setFormState={setFormState} />
      <FormButtons
        type={currency ? 'update' : 'create'}
        toggleActive={toggleActive}
        active={currency?.active}
        disabled={disabled}
        onSubmit={onSubmit}
      />
    </form>
  );
}

export default CurrencyForm;
